import { filter, map } from 'lodash-es'
import { FC, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { cn } from '@/lib/utils'
import { setFestivalName } from '../../app-reducer'
import { MenuItem } from '../../core/constants/menu'
import store, { useAppDispatch } from '../../store'
import { getMenuIcon } from '../constants/menu-icons'
import { Icon } from './Icon'

interface SidebarMenuItemProps {
  item: MenuItem
  onDropdownOpen?: () => void
  onDropdownClose?: () => void
  onClick?: () => void
}

export const SidebarMenuItem: FC<SidebarMenuItemProps> = ({
  item,
  onDropdownOpen,
  onDropdownClose,
  onClick,
}) => {
  const { pathname } = useLocation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const hasActiveChild = () => {
    if (!item.children || item.children.length === 0) return false
    return item.children.some((child) => {
      const childHref = getHref({
        ...child,
        festivalItem: item.festivalItem,
        festivalName: item.festivalName,
        editionId: item.editionId,
      })
      return pathname === childHref || pathname.startsWith(childHref + '/')
    })
  }

  const getHref = (menuItem: MenuItem) => {
    switch (menuItem.title) {
      case 'Festivals':
        return '/settings/festivals'
      case 'Admins':
        return '/settings/admins'
      case 'Dashboard':
        return '/'
      case 'Tickets':
        if (menuItem.festivalItem?.id) {
          const activeEditions = filter(
            menuItem.festivalItem.editions,
            (edition) => edition.active === true,
          )
          const editionId =
            activeEditions.length > 0
              ? activeEditions[0].id
              : menuItem.festivalItem.editions[0].id
          const categoryId =
            store.getState().tickets.getTicketCategory[editionId]?.id
          if (!!categoryId) {
            return `${menuItem.path}/${menuItem.festivalItem.platformName}/${editionId}/${categoryId}`
          } else {
            return `/tickets/categories/${menuItem.festivalName}/${editionId}`
          }
        } else {
          return '/'
        }
      case 'Settings':
        return '/settings'
      case 'Groups':
        return '/groups'
      default:
        const activeEditions = filter(
          menuItem.festivalItem?.editions,
          (edition) => edition.active === true,
        )
        const editionId =
          activeEditions.length > 0
            ? activeEditions[0].id
            : menuItem.festivalItem?.editions[0]?.id
        if (menuItem.festivalItem?.platformName && editionId) {
          return `${menuItem.path}/${menuItem.festivalItem.platformName}/${editionId}`
        }
        return menuItem.path
    }
  }

  const [isExpanded, setIsExpanded] = useState(hasActiveChild())

  const isActive = () => {
    if (!item.children || item.children.length === 0) {
      return pathname === item.path || pathname.startsWith(item.path + '/')
    } else {
      return item.children.some((child) => {
        const childHref = getHref({
          ...child,
          festivalItem: item.festivalItem,
          festivalName: item.festivalName,
          editionId: item.editionId,
        })
        return pathname === childHref || pathname.startsWith(childHref + '/')
      })
    }
  }

  const handleClick = () => {
    const hasVisibleChildren =
      item.children &&
      item.children.filter((ch) => ch.view !== false).length > 0

    if (hasVisibleChildren) {
      setIsExpanded(!isExpanded)
      if (isExpanded) {
        onDropdownClose?.()
      } else {
        onDropdownOpen?.()
      }
    } else {
      const href = getHref(item)
      dispatch(setFestivalName({ festivalName: item.festivalName }))
      navigate(href)
      onDropdownClose?.()
    }
  }

  const visibleChildren = item.children?.filter((ch) => ch.view !== false) || []
  const active = isActive()

  return (
    <div className="w-full">
      <button
        onClick={onClick ? onClick : handleClick}
        className={cn(
          `flex items-center w-full gap-[10px] transition-all text-monochrome-1 cursor-pointer py-[15px]`,
          active
            ? 'text-accent-1 border-b border-b-accent-1'
            : 'border-b border-b-monochrome-120 hover:text-monochrome-2',
        )}
      >
        <Icon
          icon={getMenuIcon(item.title)}
          width="18"
          height="18"
          viewBox="0 0 20 20"
          className="text-accent-1"
          fill="none"
        />
        <span className="text-button-m flex-1 text-left">{item.title}</span>
        {visibleChildren.length > 0 && (
          <Icon
            icon="chevron-down"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            className={cn(
              'w-4 h-4 transition-transform duration-300',
              isExpanded && 'rotate-180',
            )}
            fill="none"
          />
        )}
      </button>
      {isExpanded && visibleChildren.length > 0 && (
        <div className="pl-2 pb-2">
          {map(visibleChildren, (child, index) => {
            const childHref = getHref({
              ...child,
              festivalItem: item.festivalItem,
              festivalName: item.festivalName,
              editionId: item.editionId,
            })
            const isChildActive =
              pathname === childHref || pathname.startsWith(childHref + '/')

            // Check if child has nested children
            const nestedChildren =
              child.children?.filter((nc) => nc.view !== false) || []

            return (
              <div key={`child-${index}`} className="mt-1">
                {nestedChildren.length > 0 ? (
                  <div>
                    <div className="py-1.5 text-white/50 text-xs font-medium uppercase tracking-wider">
                      {child.title}
                    </div>
                    {map(nestedChildren, (nestedChild, nestedIndex) => {
                      const nestedHref = getHref({
                        ...nestedChild,
                        festivalItem: item.festivalItem,
                        festivalName: item.festivalName,
                        editionId: item.editionId,
                      })
                      const isNestedActive =
                        pathname === nestedHref ||
                        pathname.startsWith(nestedHref + '/')

                      return (
                        <Link
                          key={`nested-${nestedIndex}`}
                          to={nestedHref}
                          onClick={() => {
                            dispatch(
                              setFestivalName({
                                festivalName: item.festivalName,
                              }),
                            )
                            onDropdownClose?.()
                          }}
                          className={cn(
                            'flex items-center py-2 pl-3 rounded-lg text-sm transition-colors no-underline',
                            isNestedActive
                              ? 'text-accent-1 bg-white/5'
                              : 'text-white/70 hover:text-white hover:bg-white/5',
                          )}
                        >
                          {nestedChild.title}
                        </Link>
                      )
                    })}
                  </div>
                ) : (
                  <Link
                    to={childHref}
                    onClick={() => {
                      dispatch(
                        setFestivalName({ festivalName: item.festivalName }),
                      )
                      onDropdownClose?.()
                    }}
                    className={cn(
                      'flex items-center py-2 pl-3 rounded-lg text-sm transition-colors no-underline',
                      isChildActive
                        ? 'text-accent-1 bg-white/5'
                        : 'text-white/70 hover:text-white hover:bg-white/5',
                    )}
                  >
                    {child.title}
                  </Link>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
